import { useState, useEffect } from 'react';
import { supabase, checkSupabaseConnection } from '../supabase';
import type { Database } from '../database.types';

export function useWorkOrders() {
  const [workOrders, setWorkOrders] = useState<Database['public']['Tables']['work_orders']['Row'][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<{ [key: string]: boolean }>({});
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    async function fetchWorkOrders() {
      try {
        if (!initialized) {
          setInitialized(true);
          return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setError('Not authenticated');
          setWorkOrders([]);
          setLoading(false);
          return;
        }

        const { ok, error: connectionError } = await checkSupabaseConnection();
        if (!ok) {
          console.warn('Database connection check failed:', connectionError);
          setError('Having trouble connecting to the database. Please try again.');
          setWorkOrders([]);
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('work_orders')
          .select(`
            *,
            customers (
              first_name, last_name, email, phone,
              address, city, state, zip_code
            )
          `)
          .eq('company_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setWorkOrders(data || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching work orders:', err);
        const errorMessage = err instanceof Error 
          ? err.message 
          : 'An unexpected error occurred while fetching work orders';
        setError(errorMessage);
        setWorkOrders([]);
      } finally {
        setLoading(false);
      }
    }

    fetchWorkOrders();
  }, [initialized]);

  const addWorkOrder = async (workOrderData: Omit<Database['public']['Tables']['work_orders']['Insert'], 'company_id' | 'id'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('work_orders')
        .insert([{ ...workOrderData, company_id: user.id }])
        .select()
        .single();

      if (error) throw error;

      setWorkOrders(prev => [data, ...prev]);
      return data;
    } catch (err) {
      throw err;
    }
  };

  const deleteWorkOrder = async (id: string) => {
    try {
      setDeleting(prev => ({ ...prev, [id]: true }));
      const { error } = await supabase
        .from('work_orders')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setWorkOrders(prev => prev.filter(wo => wo.id !== id));
    } catch (err) {
      throw err;
    } finally {
      setDeleting(prev => ({ ...prev, [id]: false }));
    }
  };

  return { workOrders, loading, error, deleting, addWorkOrder, deleteWorkOrder };
}